import { FC, useState, Suspense, lazy } from 'react';
import { GridCol } from 'dibs-elements/exports/Grid';
import { graphql, useFragment } from 'react-relay';
import {
    Currency,
    PdpRespRecentSales_item$key,
} from './__generated__/PdpRespRecentSales_item.graphql';
import { PdpRespRecentSales_user$key } from './__generated__/PdpRespRecentSales_user.graphql';

import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { title, tooltip } from './../messages/pdpRecentSalesMessages';
import InfoCircle from 'dibs-icons/exports/InfoCircle';
import InfoCircleFilled from 'dibs-icons/exports/InfoCircleFilled';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import { getDisplayPriceAmount } from '../helpers/getItemDisplayPrice';
import { trackRecentSalesModuleTooltipView } from '../PdpSharedRecentSales/recentSalesTracking';

// styles
import styles from './PdpRespRecentSales.scss';

const PdpSharedRecentSales = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedRecentSalesLazy" */ '../PdpSharedRecentSales/PdpSharedRecentSales'
        )
);

type PdpRespRecentSalesProps = {
    load: boolean;
    currency: Currency;
    item: PdpRespRecentSales_item$key;
    user: PdpRespRecentSales_user$key | null;
    itemId: string;
    // TOF-330: Cleanup when feature flag is removed
    enableMakeOfferButton?: boolean;
    // TOF-171: Cleanup when feature flag is removed
    showRareFindTreatment: boolean;
};

export const PdpRespRecentSales: FC<PdpRespRecentSalesProps> = ({
    currency,
    load,
    item: itemRef,
    user: userRef,
    itemId,
    enableMakeOfferButton,
    showRareFindTreatment,
}) => {
    const [showTooltip, setShowTooltip] = useState(false);

    const item = useFragment(
        graphql`
            fragment PdpRespRecentSales_item on Item {
                ...PdpSharedRecentSales_item
                similarRecentlySoldItemsPrices(currency: $currency) {
                    priceRange
                }
                displayPrice(
                    isTrade: $isTrade
                    buyerId: $userId
                    userCountryCode: $userCountryCode
                ) {
                    convertedAmountList {
                        amount
                        currency
                    }
                }
            }
        `,
        itemRef
    );

    const user = useFragment(
        graphql`
            fragment PdpRespRecentSales_user on User {
                ...PdpSharedRecentSales_user
            }
        `,
        userRef
    );

    const { similarRecentlySoldItemsPrices, displayPrice = null } = item;

    if (!load) {
        return null;
    }

    const supportedCurrencies = ['USD', 'EUR', 'GBP']; // hide module if other display currencies are selected due to price conversion issues
    if (!supportedCurrencies.includes(currency)) {
        return null;
    }

    if (!similarRecentlySoldItemsPrices) {
        return null;
    }

    // TODO: remove after A/B test SDATA-1937
    const price = getDisplayPriceAmount(displayPrice, currency);

    if (!price) {
        return null;
    }

    const { priceRange } = similarRecentlySoldItemsPrices;

    const onShowTooltip = (): void => {
        setShowTooltip(true);
        trackRecentSalesModuleTooltipView(priceRange);
    };

    return (
        <Suspense fallback="">
            <HeadingLevel>
                {Heading => (
                    <GridCol desktop={4} mobile={12}>
                        <div data-tn="pdp-details-recent-sales" className={`${styles.wrapper}`}>
                            <div className={styles.header}>
                                <Heading className={styles.title}>{title}</Heading>
                                <div
                                    onMouseEnter={onShowTooltip}
                                    onMouseLeave={() => setShowTooltip(false)}
                                    className={styles.tooltip}
                                >
                                    <div className={styles.tooltipTrigger}>
                                        {showTooltip ? (
                                            <InfoCircleFilled className={styles.infoIcon} />
                                        ) : (
                                            <InfoCircle className={styles.infoIcon} />
                                        )}
                                    </div>
                                    <Tooltip isVisible={showTooltip} direction="right">
                                        <div
                                            className={styles.tooltipContent}
                                            id="pdp.pdpRespRecentSales.tooltip"
                                        >
                                            {tooltip}
                                        </div>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className={styles.contentBox}>
                                <PdpSharedRecentSales
                                    currency={currency}
                                    item={item}
                                    user={user}
                                    itemId={itemId}
                                    enableMakeOfferButton={enableMakeOfferButton}
                                    showRareFindTreatment={showRareFindTreatment}
                                />
                            </div>
                        </div>
                    </GridCol>
                )}
            </HeadingLevel>
        </Suspense>
    );
};
